import * as amplitude from "@amplitude/analytics-browser"
import type {
    BrowserClient,
    BrowserOptions,
    Plugin,
} from "@amplitude/analytics-core"

import {
    AmplitudeEvent,
    COMMON_AMPLITUDE_CONFIG,
} from "@common-shared/config/amplitude.config"
import type { IIndexable } from "@common-shared/types/general.types"

import { UtilHelper } from "@front-shared/helpers/util.helper"

import {
    AMPLITUDE_CONFIG,
    AmplitudePageViewProperty,
} from "../config/amplitude.config"
import { GENERAL_CONFIG } from "../config/general.config"
import { ROUTES_CONFIG } from "../config/routes.config"
import { AuthHelper } from "./auth.helper"

let amplitudeClient: BrowserClient

export class AmplitudeHelper {
    /**
     * Return the Amplitude browser client, initializing it opted out with
     * identified-only page view autocapture. Null for demo sessions or a missing API key.
     */
    static getInstance(): BrowserClient | null {
        // Don't allow frontend Amplitude for demo sessions
        if (AuthHelper.isDemoSessionActive()) {
            return null
        }

        if (!amplitudeClient) {
            const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY

            // Check if API key is present
            if (!apiKey) {
                !GENERAL_CONFIG.isLocal &&
                    console.warn(
                        "Amplitude API key not found. Amplitude tracking disabled.",
                    )

                return null
            }

            try {
                amplitudeClient = amplitude.createInstance()
                amplitudeClient.add(AmplitudeHelper.sensitiveDataPlugin)

                const options: BrowserOptions = {
                    optOut: true,
                    logLevel: 0,
                    // Remote config would overwrite local autocapture,
                    // including pageViews.trackOn.
                    fetchRemoteConfig: false,
                    autocapture: {
                        pageViews: {
                            trackOn: () =>
                                AmplitudeHelper.shouldCapturePageView(),
                            trackHistoryChanges: "pathOnly",
                        },
                        attribution: false,
                        fileDownloads: false,
                        formInteractions: false,
                        sessions: false,
                        elementInteractions: false,
                        frustrationInteractions: false,
                        networkTracking: false,
                        webVitals: false,
                    },
                }

                amplitudeClient.init(apiKey, options)
            } catch (error) {
                console.error("Failed to initialize Amplitude:", error)
                return null
            }
        }

        return amplitudeClient
    }

    /**
     * Bind the prefixed user id, set user properties, enable tracking, and
     * record a page view when the identity is new or tracking was opted out
     * (init and reload restore userId from cookie while still opted out).
     */
    static identify(userId: string | number, properties: IIndexable = {}) {
        const instance = AmplitudeHelper.getInstance()

        if (!instance) {
            return
        }

        try {
            const resolvedUserId = AmplitudeHelper.toAmplitudeUserId(userId)
            const previousUserId = instance.getUserId()
            const isNewIdentity = previousUserId !== resolvedUserId

            // SDK starts opted out on each page load and after logout;
            // first identify re-enables it
            const wasOptedOut = instance.getOptOut() === true

            if (isNewIdentity && previousUserId) {
                instance.setOptOut(true)
                instance.reset()
            }

            instance.setUserId(resolvedUserId)
            instance.setOptOut(false)

            if (Object.keys(properties).length > 0) {
                const identify = new amplitude.Identify()

                Object.entries(properties).forEach(([key, value]) => {
                    identify.set(key, value)
                })

                instance.identify(identify)
            }

            if (isNewIdentity || wasOptedOut) {
                AmplitudeHelper.trackCurrentPageView()
            }
        } catch (error) {
            console.error("Error in identify:", error)
        }
    }

    /**
     * Log a custom event for the identified user. No-ops without a user id.
     */
    static logEvent(
        event: AmplitudeEvent,
        properties: IIndexable,
        userId?: string | number,
    ) {
        const instance = AmplitudeHelper.getInstance()

        if (!instance) {
            return
        }

        const resolvedUserId = userId
            ? AmplitudeHelper.toAmplitudeUserId(userId)
            : instance.getUserId()

        if (!resolvedUserId) {
            return
        }

        try {
            instance.logEvent(String(event), properties, {
                user_id: resolvedUserId,
                device_id: instance.getDeviceId(),
            })
        } catch (error) {
            console.error("Error logging event:", error)
        }
    }

    /**
     * Reset the Amplitude user and opt out until the next identify.
     */
    static clearUser() {
        const instance = AmplitudeHelper.getInstance()

        instance?.reset()

        instance?.setOptOut(true)
    }

    /**
     * Prefix a user id with `user_` unless it already has the prefix.
     */
    private static toAmplitudeUserId(userId: string | number) {
        const value = String(userId)

        return value.startsWith(COMMON_AMPLITUDE_CONFIG.amplitudeUserIdPrefix)
            ? value
            : `${COMMON_AMPLITUDE_CONFIG.amplitudeUserIdPrefix}${value}`
    }

    /**
     * Whether the current path is under the auth router.
     */
    private static isAuthRoute() {
        return window.location.pathname.startsWith(ROUTES_CONFIG.authUrl)
    }

    /**
     * Whether autocapture should emit a page view for the current route.
     */
    private static shouldCapturePageView() {
        const instance = AmplitudeHelper.getInstance()

        return Boolean(
            instance?.getUserId() &&
                instance.getOptOut() !== true &&
                !AmplitudeHelper.isAuthRoute(),
        )
    }

    /**
     * Log a page view for the current path after identify, when autocapture
     * would have skipped the landing page (SDK started opted out).
     */
    private static trackCurrentPageView() {
        const instance = AmplitudeHelper.getInstance()
        const userId = instance?.getUserId()

        if (!instance || !userId || AmplitudeHelper.isAuthRoute()) {
            return
        }

        try {
            instance.logEvent(
                AMPLITUDE_CONFIG.pageViewedEvent,
                AmplitudeHelper.getPageViewProperties(),
                {
                    user_id: userId,
                    device_id: instance.getDeviceId(),
                },
            )
        } catch (error) {
            console.error("Error logging page view:", error)
        }
    }

    /**
     * Drop anonymous and auth-route events. For page views, strip stale titles
     * and query strings (report filters are PHI) from location and referrer.
     */
    private static get sensitiveDataPlugin(): Plugin {
        return {
            name: "sensitive-data-filter",
            type: "enrichment",
            setup: () => Promise.resolve(),
            execute: async event => {
                if (event.event_type === AMPLITUDE_CONFIG.identifyEvent) {
                    return event
                }

                if (!event.user_id) {
                    return null
                }

                if (AmplitudeHelper.isAuthRoute()) {
                    return null
                }

                const properties = event.event_properties as
                    | IIndexable
                    | undefined

                // The SDK reads document.title on pushState, before React
                // commits the new route's title, so page views carry the
                // previous page's title (e.g. "Reports" on a /insights event).
                // Path is accurate; title is not.
                // Page Location is the full href (query included). Report
                // filters in ?filters= JSON are PHI, so use query-free Page URL.
                // Campaign parser also copies document.referrer onto page views.
                if (
                    event.event_type === AMPLITUDE_CONFIG.pageViewedEvent &&
                    properties
                ) {
                    const { [AMPLITUDE_CONFIG.pageTitleProperty]: _, ...rest } =
                        properties

                    const pageUrl = rest[AmplitudePageViewProperty.PAGE_URL]
                    const pageLocation =
                        rest[AmplitudePageViewProperty.PAGE_LOCATION]

                    rest[AmplitudePageViewProperty.PAGE_LOCATION] =
                        typeof pageUrl === "string" && pageUrl
                            ? pageUrl
                            : AmplitudeHelper.toQueryFreeUrl(
                                  String(pageLocation ?? ""),
                              )

                    const referrer = rest[AMPLITUDE_CONFIG.pageReferrerProperty]

                    if (typeof referrer === "string") {
                        rest[AMPLITUDE_CONFIG.pageReferrerProperty] =
                            AmplitudeHelper.toQueryFreeUrl(referrer)
                    }

                    event.event_properties = rest
                }

                if (event.event_type === AMPLITUDE_CONFIG.elementClickedEvent) {
                    const elementHierarchy = properties?.[
                        "[Amplitude] Element Hierarchy"
                    ] as { classes: string[] }[]

                    if (
                        elementHierarchy?.some(element =>
                            element.classes?.includes("mouseflow-ignore"),
                        )
                    ) {
                        event.event_properties = {
                            ...properties,
                            "[Amplitude] Element Text":
                                AMPLITUDE_CONFIG.redactedValue,
                        }
                    }
                }

                return event
            },
        }
    }

    /**
     * Page view properties from origin + pathname only. Report filters live in
     * the query string and are treated as PHI.
     */
    private static getPageViewProperties(): IIndexable {
        const pageUrl = UtilHelper.decodeUriValueSafely(
            `${window.location.origin}${window.location.pathname}`,
        )

        const pagePath = UtilHelper.decodeUriValueSafely(
            window.location.pathname,
        )

        return {
            [AmplitudePageViewProperty.PAGE_DOMAIN]: window.location.hostname,
            [AmplitudePageViewProperty.PAGE_LOCATION]: pageUrl,
            [AmplitudePageViewProperty.PAGE_PATH]: pagePath,
            [AmplitudePageViewProperty.PAGE_URL]: pageUrl,
        }
    }

    /**
     * Origin + pathname only, so query-string report filters are not sent.
     */
    private static toQueryFreeUrl(url: string): string {
        const decoded = UtilHelper.decodeUriValueSafely(url)

        try {
            const parsed = new URL(decoded)

            return `${parsed.origin}${parsed.pathname}`
        } catch {
            return decoded.split("?")[0]
        }
    }
}
